import { useEffect, useMemo, useState } from 'react';
import { calmTokens as t } from '../../shared/tokens';
import CalmModal from '../shared/CalmModal';
import { MONTHS_FR_SHORT } from '../../shared/locale';
import { roundCents } from '../../shared/recurrence';
import {
  IconArrowDown,
  IconArrowUp,
  IconCalendar,
  IconCheck,
  IconTrash,
  IconX,
} from '../../shared/icons';

const RECURRENCE_OPTIONS = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
  { value: 'custom', label: 'Personnalisé' },
];

function toMonthInput(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fromMonthInput(v) {
  return v ? `${v}-01` : '';
}

function defaultForm() {
  const todayMonth = toMonthInput(new Date());
  return {
    title: '',
    type: 'debit',
    amount: '',
    dayOfMonth: 1,
    recurrenceType: 'monthly',
    yearlyMonth: new Date().getMonth(),
    customMonths: [new Date().getMonth()],
    quarterlyMonths: (() => {
      const m = new Date().getMonth();
      const start = m % 3;
      return [start, start + 3, start + 6, start + 9];
    })(),
    isActive: true,
    startMonth: todayMonth,
    endMonth: '',
    withdrawnFromPersonal: true,
    withdrawnFromBank: false,
    category: 'uncategorized',
  };
}

function templateToForm(tpl) {
  if (!tpl) return defaultForm();
  const rp = tpl.recurrencePattern || {};
  const vp = tpl.validityPeriod || {};
  const currentMonth = new Date().getMonth();
  return {
    title: tpl.title || '',
    type: tpl.type || 'debit',
    amount: tpl.amount != null ? String(tpl.amount) : '',
    dayOfMonth: Number(tpl.dayOfMonth) || 1,
    recurrenceType: rp.type || 'monthly',
    yearlyMonth: rp.month ?? currentMonth,
    customMonths: Array.isArray(rp.customMonths) && rp.customMonths.length
      ? rp.customMonths
      : [currentMonth],
    quarterlyMonths: Array.isArray(rp.months) && rp.months.length
      ? rp.months
      : (() => {
          const start = currentMonth % 3;
          return [start, start + 3, start + 6, start + 9];
        })(),
    isActive: tpl.isActive !== false,
    startMonth: toMonthInput(vp.startDate) || toMonthInput(new Date()),
    endMonth: toMonthInput(vp.endDate),
    withdrawnFromPersonal: tpl.withdrawnFromPersonal !== false,
    withdrawnFromBank: Boolean(tpl.withdrawnFromBank),
    category: tpl.category || 'uncategorized',
  };
}

function Field({ label, children, hint, cols = 1 }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        gridColumn: `span ${cols}`,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: t.textMute,
          textTransform: 'uppercase',
          letterSpacing: 1,
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span
          style={{
            fontSize: 11,
            color: t.textDim,
            marginTop: -2,
            letterSpacing: -0.05,
          }}
        >
          {hint}
        </span>
      )}
    </label>
  );
}

function Segment({ options, value, onChange }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 10,
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange?.(opt.value)}
            style={{
              padding: '7px 14px',
              background: active ? t.surface2 : 'transparent',
              border: active ? `1px solid ${t.borderS}` : '1px solid transparent',
              borderRadius: 8,
              color: active ? t.text : t.textDim,
              fontSize: 12,
              fontWeight: active ? 500 : 400,
              fontFamily: t.fontUi,
              letterSpacing: -0.1,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all .15s',
            }}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function DayGrid({ selected, onChange }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(10, 1fr)',
        gap: 4,
        padding: 10,
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 10,
      }}
    >
      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
        const active = selected === d;
        return (
          <button
            key={d}
            type="button"
            onClick={() => onChange?.(d)}
            style={{
              aspectRatio: '1',
              minWidth: 0,
              background: active ? t.accent : 'transparent',
              border: `1px solid ${active ? t.accent : t.borderS}`,
              borderRadius: 7,
              color: active ? t.bg : t.textDim,
              fontFamily: t.fontMono,
              fontSize: 11,
              fontWeight: active ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 120ms',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {String(d).padStart(2, '0')}
          </button>
        );
      })}
    </div>
  );
}

function MonthGrid({ selected, onChange, single = false }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 5,
        padding: 10,
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 10,
      }}
    >
      {MONTHS_FR_SHORT.map((m, i) => {
        const active = single ? selected === i : (selected || []).includes(i);
        return (
          <button
            key={i}
            type="button"
            onClick={() => {
              if (single) {
                onChange?.(i);
              } else {
                const s = new Set(selected || []);
                if (s.has(i)) s.delete(i);
                else s.add(i);
                onChange?.(Array.from(s).sort((a, b) => a - b));
              }
            }}
            style={{
              padding: '9px 0',
              background: active ? t.accent : 'transparent',
              border: `1px solid ${active ? t.accent : t.borderS}`,
              borderRadius: 7,
              color: active ? t.bg : t.textDim,
              fontFamily: t.fontUi,
              fontSize: 11,
              fontWeight: active ? 600 : 400,
              cursor: 'pointer',
              textTransform: 'capitalize',
              transition: 'all 120ms',
            }}
          >
            {m}
          </button>
        );
      })}
    </div>
  );
}

const inputRowStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  background: t.bg,
  border: `1px solid ${t.border}`,
  borderRadius: 10,
  padding: '0 12px',
  height: 40,
};

const bareInputStyle = {
  flex: 1,
  height: '100%',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: t.text,
  fontFamily: t.fontUi,
  fontSize: 13,
  letterSpacing: -0.1,
};

export default function SettingsTemplateModal({
  open,
  mode = 'create',
  template,
  onClose,
  onSave,
  onDelete,
  loading = false,
}) {
  const [form, setForm] = useState(() => templateToForm(template));
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(templateToForm(template));
      setSubmitError('');
    }
  }, [open, template]);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const titleAccent = useMemo(() => form.title?.trim() || 'cette récurrente', [form.title]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const amountNum = parseFloat(String(form.amount).replace(',', '.'));
    if (!form.title.trim()) {
      setSubmitError('Donnez un intitulé à la récurrente.');
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setSubmitError('Indiquez un montant positif.');
      return;
    }
    if (!form.startMonth) {
      setSubmitError('Précisez le début de validité.');
      return;
    }

    const recurrencePattern = {
      type: form.recurrenceType,
      month: null,
      months: [],
      customMonths: [],
    };
    if (form.recurrenceType === 'yearly') {
      recurrencePattern.month = Number(form.yearlyMonth);
    } else if (form.recurrenceType === 'quarterly') {
      recurrencePattern.months = [...form.quarterlyMonths];
    } else if (form.recurrenceType === 'custom') {
      recurrencePattern.customMonths = [...form.customMonths];
    }

    const payload = {
      title: form.title.trim(),
      type: form.type,
      amount: roundCents(amountNum),
      dayOfMonth: Math.max(1, Math.min(31, Number(form.dayOfMonth) || 1)),
      category: form.category || 'uncategorized',
      withdrawnFromPersonal: Boolean(form.withdrawnFromPersonal),
      withdrawnFromBank: Boolean(form.withdrawnFromBank),
      isActive: Boolean(form.isActive),
      recurrencePattern,
      validityPeriod: {
        startDate: fromMonthInput(form.startMonth),
        ...(form.endMonth ? { endDate: fromMonthInput(form.endMonth) } : {}),
      },
    };

    onSave?.(payload);
  };

  const header = (
    <>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(165,180,252,0.10), transparent 60%)',
            borderRadius: 20,
          }}
        />
        <div
          style={{
            padding: 'var(--modal-head-pad)',
            borderBottom: `1px solid ${t.border}`,
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                color: t.textMute,
                textTransform: 'uppercase',
                letterSpacing: 1.4,
                fontWeight: 500,
                marginBottom: 6,
              }}
            >
              {mode === 'create' ? 'Nouvelle récurrente' : 'Modifier la récurrente'}
            </div>
            <h2
              id="settings-tpl-title"
              style={{
                fontFamily: t.fontDisp,
                fontSize: 'var(--modal-title)',
                fontWeight: 400,
                margin: 0,
                letterSpacing: -0.5,
                lineHeight: 1.15,
                color: t.text,
              }}
            >
              {mode === 'create' ? (
                <>
                  Un{' '}
                  <span style={{ fontStyle: 'italic', color: t.accent }}>
                    rendez-vous
                  </span>{' '}
                  récurrent.
                </>
              ) : (
                <>
                  Ajuster{' '}
                  <span style={{ fontStyle: 'italic', color: t.accent }}>
                    « {titleAccent} »
                  </span>
                </>
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Fermer"
            title="Fermer"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'transparent',
              border: `1px solid ${t.border}`,
              color: t.textDim,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'grid',
              placeItems: 'center',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <IconX size={14} />
          </button>
        </div>
    </>
  );

  const footer = (
        <div
          style={{
            padding: 'var(--modal-foot-pad)',
            borderTop: `1px solid ${t.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: 12, color: t.textDim }}>
            {mode === 'edit' && onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: t.debit,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  padding: 0,
                  fontFamily: t.fontUi,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <IconTrash size={12} />
                Supprimer cette récurrente
              </button>
            ) : (
              <span>La récurrente sera prise en compte dès le prochain calcul.</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                border: `1px solid ${t.border}`,
                borderRadius: 999,
                color: t.textDim,
                fontSize: 13,
                fontFamily: t.fontUi,
                cursor: loading ? 'not-allowed' : 'pointer',
                letterSpacing: -0.1,
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 18px',
                background: t.text,
                color: t.bg,
                border: 'none',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: t.fontUi,
                cursor: loading ? 'progress' : 'pointer',
                letterSpacing: -0.1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                opacity: loading ? 0.7 : 1,
              }}
            >
              <IconCheck size={13} />
              {loading
                ? 'En cours…'
                : mode === 'create'
                  ? 'Créer'
                  : 'Enregistrer'}
            </button>
          </div>
        </div>
  );

  return (
    <CalmModal
      open={open}
      as="form"
      onSubmit={handleSubmit}
      onClose={onClose}
      labelledBy="settings-tpl-title"
      zIndex={60}
      maxWidth={640}
      dismissible={!loading}
      backdropClassName="settings-modal-backdrop"
      cardClassName="settings-modal-card"
      cardStyle={{
        background: t.bg,
        color: t.text,
        borderRadius: 20,
        boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
        position: 'relative',
      }}
      bodyStyle={{ padding: 0 }}
      header={header}
      footer={footer}
    >
        <div
          className="settings-tpl-grid"
          style={{
            padding: 'var(--modal-body-pad)',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: 20,
            position: 'relative',
          }}
        >
          <Field label="Nature" cols={2}>
            <Segment
              value={form.type}
              onChange={(v) => up('type', v)}
              options={[
                {
                  value: 'credit',
                  label: 'Entrée',
                  icon: <IconArrowDown size={12} style={{ color: t.credit }} />,
                },
                {
                  value: 'debit',
                  label: 'Sortie',
                  icon: <IconArrowUp size={12} style={{ color: t.debit }} />,
                },
              ]}
            />
          </Field>

          <Field label="Intitulé" cols={2}>
            <div style={inputRowStyle}>
              <input
                type="text"
                value={form.title}
                onChange={(e) => up('title', e.target.value)}
                placeholder={
                  form.type === 'credit'
                    ? 'Salaire, dividendes, loyer perçu…'
                    : 'Loyer, Netflix, EDF, Urssaf…'
                }
                style={bareInputStyle}
                required
              />
            </div>
          </Field>

          <Field
            label="Montant"
            cols={2}
            hint="Positif. Le signe est donné par la nature."
          >
            <div style={inputRowStyle}>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => up('amount', e.target.value)}
                placeholder="0,00"
                style={{
                  ...bareInputStyle,
                  fontFamily: t.fontMono,
                  fontSize: 14,
                  letterSpacing: -0.3,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
                required
              />
              <span style={{ color: t.textDim, fontSize: 13, marginLeft: 6 }}>€</span>
            </div>
          </Field>

          <Field label="Fréquence" cols={2}>
            <Segment
              value={form.recurrenceType}
              onChange={(v) => up('recurrenceType', v)}
              options={RECURRENCE_OPTIONS}
            />
          </Field>

          {form.recurrenceType === 'yearly' && (
            <Field label="Mois de l'année" cols={2}>
              <MonthGrid
                single
                selected={form.yearlyMonth}
                onChange={(v) => up('yearlyMonth', v)}
              />
            </Field>
          )}

          {form.recurrenceType === 'custom' && (
            <Field
              label="Mois concernés"
              cols={2}
              hint={`${form.customMonths.length} mois sélectionné${
                form.customMonths.length > 1 ? 's' : ''
              } par an`}
            >
              <MonthGrid
                selected={form.customMonths}
                onChange={(v) => up('customMonths', v)}
              />
            </Field>
          )}

          <Field
            label="Jour du mois"
            cols={2}
            hint="Le jour où l'opération tombe habituellement."
          >
            <DayGrid selected={form.dayOfMonth} onChange={(v) => up('dayOfMonth', v)} />
          </Field>

          <Field label="Début de validité" hint="Mois à partir duquel c'est actif.">
            <div style={inputRowStyle}>
              <input
                type="month"
                value={form.startMonth}
                onChange={(e) => up('startMonth', e.target.value)}
                style={bareInputStyle}
                required
              />
              <IconCalendar size={12} style={{ color: t.textDim, marginLeft: 6 }} />
            </div>
          </Field>
          <Field label="Fin de validité" hint="Optionnel — laisser vide pour illimité.">
            <div style={inputRowStyle}>
              <input
                type="month"
                value={form.endMonth}
                onChange={(e) => up('endMonth', e.target.value)}
                style={bareInputStyle}
              />
              <IconCalendar size={12} style={{ color: t.textDim, marginLeft: 6 }} />
            </div>
          </Field>

          <Field label="État" cols={2}>
            <button
              type="button"
              onClick={() => up('isActive', !form.isActive)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 14px',
                background: t.bg,
                border: `1px solid ${t.border}`,
                borderRadius: 10,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                color: t.text,
                fontFamily: t.fontUi,
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 20,
                  borderRadius: 999,
                  background: form.isActive
                    ? 'rgba(134,239,172,0.25)'
                    : t.border,
                  border: `1px solid ${
                    form.isActive ? 'rgba(134,239,172,0.4)' : t.borderS
                  }`,
                  position: 'relative',
                  flexShrink: 0,
                  transition: 'all 160ms',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: form.isActive ? 16 : 2,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: form.isActive ? t.credit : t.textDim,
                    transition: 'all 160ms',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: -0.1,
                  }}
                >
                  {form.isActive ? 'Actif' : 'En pause'}
                </div>
                <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>
                  {form.isActive
                    ? 'Compté dans les prévisions du mois.'
                    : 'Conservé mais ignoré dans les prévisions.'}
                </div>
              </div>
            </button>
          </Field>

          {submitError && (
            <div
              style={{
                gridColumn: 'span 2',
                padding: '10px 14px',
                background: t.debitDim,
                border: `1px solid ${t.debitBorder}`,
                borderRadius: 10,
                fontSize: 12,
                color: t.debit,
              }}
            >
              {submitError}
            </div>
          )}
        </div>

      <style>{`
        .settings-modal-backdrop {
          animation: settingsModalFade .18s ease-out both;
        }
        .settings-modal-card {
          animation: settingsModalRise .22s cubic-bezier(.2,.8,.2,1) both;
        }
        @keyframes settingsModalFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes settingsModalRise {
          from { opacity: 0; transform: translateY(8px) scale(.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (max-width: 640px) {
          .settings-tpl-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .settings-modal-backdrop,
          .settings-modal-card { animation: none !important; }
        }
      `}</style>
    </CalmModal>
  );
}
